import { useEffect, useState } from 'react'

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('velink_token')}` }
}

interface Company {
  id: string; name: string; email: string; siret: string | null
  phone: string | null; isSuspended: boolean; createdAt: string
  _count: { missions: number }
}

interface Provider {
  id: string; firstName: string; lastName: string; email: string
  phone: string | null; rating: number; ratingCount: number
  isSuspended: boolean; isVerified: boolean; createdAt: string
  _count: { assignments: number }
}

export default function UtilisateursPage() {
  const [tab, setTab] = useState<'companies' | 'providers'>('companies')
  const [companies, setCompanies] = useState<Company[]>([])
  const [providers, setProviders] = useState<Provider[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/admin/companies`, { headers: authHeaders() }).then((r) => r.json()),
      fetch(`${API}/api/admin/providers`, { headers: authHeaders() }).then((r) => r.json()),
    ]).then(([c, p]) => {
      setCompanies(c.items ?? [])
      setProviders(p.items ?? [])
      setLoading(false)
    })
  }, [])

  async function toggleSuspend(type: 'companies' | 'providers', id: string, current: boolean) {
    await fetch(`${API}/api/admin/${type}/${id}/suspend`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ suspend: !current }),
    })
    if (type === 'companies') {
      setCompanies((prev) => prev.map((c) => c.id === id ? { ...c, isSuspended: !current } : c))
    } else {
      setProviders((prev) => prev.map((p) => p.id === id ? { ...p, isSuspended: !current } : p))
    }
  }

  if (loading) return <div className="text-text-muted animate-pulse">Chargement…</div>

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-text-primary">Utilisateurs</h1>
        <p className="text-text-secondary mt-1">{companies.length} entreprises · {providers.length} prestataires</p>
      </div>

      <div className="flex gap-2 mb-6">
        {(['companies', 'providers'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={['px-4 py-2 rounded-xl text-sm transition-all', tab === t ? 'glass-btn-accent' : 'glass-btn'].join(' ')}
          >
            {t === 'companies' ? `Entreprises (${companies.length})` : `Prestataires (${providers.length})`}
          </button>
        ))}
      </div>

      {tab === 'companies' && (
        <div className="glass-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.08] text-text-secondary">
                <th className="text-left px-5 py-3 font-medium">Entreprise</th>
                <th className="text-left px-5 py-3 font-medium">Email</th>
                <th className="text-center px-5 py-3 font-medium">Missions</th>
                <th className="text-center px-5 py-3 font-medium">Statut</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => (
                <tr key={c.id} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                  <td className="px-5 py-3 text-text-primary font-medium">{c.name}</td>
                  <td className="px-5 py-3 text-text-secondary">{c.email}</td>
                  <td className="px-5 py-3 text-center text-text-secondary">{c._count.missions}</td>
                  <td className="px-5 py-3 text-center">
                    <span className={['text-xs px-2 py-0.5 rounded-full border', c.isSuspended ? 'bg-danger/10 border-danger/25 text-danger' : 'bg-success/10 border-success/25 text-success'].join(' ')}>
                      {c.isSuspended ? 'Suspendu' : 'Actif'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => toggleSuspend('companies', c.id, c.isSuspended)}
                      className={['glass-btn px-3 py-1 text-xs rounded-lg', c.isSuspended ? 'text-success' : 'text-danger'].join(' ')}
                    >
                      {c.isSuspended ? 'Réactiver' : 'Suspendre'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'providers' && (
        <div className="glass-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.08] text-text-secondary">
                <th className="text-left px-5 py-3 font-medium">Prestataire</th>
                <th className="text-left px-5 py-3 font-medium">Email</th>
                <th className="text-center px-5 py-3 font-medium">Note</th>
                <th className="text-center px-5 py-3 font-medium">Missions</th>
                <th className="text-center px-5 py-3 font-medium">Statut</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {providers.map((p) => (
                <tr key={p.id} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                  <td className="px-5 py-3 text-text-primary font-medium">{p.firstName} {p.lastName}</td>
                  <td className="px-5 py-3 text-text-secondary">{p.email}</td>
                  <td className="px-5 py-3 text-center text-text-secondary">
                    {p.ratingCount > 0 ? `⭐ ${p.rating.toFixed(1)}` : '–'}
                  </td>
                  <td className="px-5 py-3 text-center text-text-secondary">{p._count.assignments}</td>
                  <td className="px-5 py-3 text-center">
                    <span className={['text-xs px-2 py-0.5 rounded-full border', p.isSuspended ? 'bg-danger/10 border-danger/25 text-danger' : 'bg-success/10 border-success/25 text-success'].join(' ')}>
                      {p.isSuspended ? 'Suspendu' : 'Actif'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => toggleSuspend('providers', p.id, p.isSuspended)}
                      className={['glass-btn px-3 py-1 text-xs rounded-lg', p.isSuspended ? 'text-success' : 'text-danger'].join(' ')}
                    >
                      {p.isSuspended ? 'Réactiver' : 'Suspendre'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
